import React, { useEffect } from 'react';
import './HelpCenter.css';
import './LegalPage.css';

/* ══════════════════════════════════════════════════════════════════════════
 * Shared legal content page (Terms & Conditions / Privacy Policy).
 * Reuses the HelpCenter shell (title, typography, container) but renders a
 * single readable prose column — no topic Sidebar.
 *
 * Props:
 *   brand    — 'CLS' | 'ABC' (sets data-brand on <html> like FAQ/Contact)
 *   header   — brand header element, e.g. <Header showTopBar={false} />
 *   footer   — brand footer element, e.g. <Footer />
 *   title    — page h1
 *   sections — [{ title?, blocks: [{ type: 'p'|'ul'|'lines'|'h4', text?, items? }] }]
 * ══════════════════════════════════════════════════════════════════════════ */

const LINK_PATTERN = /(https?:\/\/[^\s,)]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;

/** Turn raw URLs / e-mail addresses inside body copy into inline links. */
const linkify = (text) =>
  text.split(LINK_PATTERN).map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={i} className="helpcenter__inline-link" href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      );
    }
    if (/^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/.test(part)) {
      return (
        <a key={i} className="helpcenter__inline-link" href={`mailto:${part}`}>
          {part}
        </a>
      );
    }
    return part;
  });

const LegalBlock = ({ block }) => {
  switch (block.type) {
    case 'h4':
      return <h3 className="legal-page__subheading typo-h4">{block.text}</h3>;
    case 'ul':
      return (
        <ul className="legal-page__list typo-body">
          {block.items.map((item, i) => (
            <li key={i}>{linkify(item)}</li>
          ))}
        </ul>
      );
    case 'lines':
      return (
        <p className="legal-page__text typo-body">
          {block.lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 ? <br /> : null}
              {linkify(line)}
            </React.Fragment>
          ))}
        </p>
      );
    case 'p':
    default:
      return <p className="legal-page__text typo-body">{linkify(block.text)}</p>;
  }
};

/** Bare section renderer — reusable outside the page shell (e.g. checkout privacy SidePanel). */
export const LegalSections = ({ sections = [] }) => (
  <>
    {sections.map((section, i) => (
      <section className="legal-page__section" key={i}>
        {section.title ? (
          <h2 className="helpcenter__section-title typo-h3">{section.title}</h2>
        ) : null}
        {section.blocks.map((block, j) => (
          <LegalBlock key={j} block={block} />
        ))}
      </section>
    ))}
  </>
);

const LegalPage = ({ brand = 'CLS', header, footer, title, sections = [] }) => {
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', brand);
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, [brand]);

  return (
    <div className="helpcenter">
      {header}
      <main className="helpcenter__main">
        <div className="helpcenter__container">
          <div className="helpcenter__list-col legal-page__col">
            <h1 className="helpcenter__title typo-h1">{title}</h1>

            <LegalSections sections={sections} />
          </div>
        </div>
      </main>
      {footer}
    </div>
  );
};

export default LegalPage;
